export interface Env {
    /**
     * The gateway every service answers behind, routing `/v1/<service>` by
     * path: `/v1/envoy`, `/v1/catalogue`, `/v1/resume` and the rest.
     * `VITE_API_URL`, with no trailing slash. Default https://api.jobenvoy.ai.
     */
    API_URL: string;
    /** The AuthKit client id. Every session is a WorkOS sign-in; there is no other way in. */
    WORKOS_CLIENT_ID: string;
    /** A custom AuthKit domain, without the scheme. */
    WORKOS_API_HOSTNAME: string;
    /**
     * Placeholder data instead of the API, and a placeholder person instead
     * of a sign-in. For looking at the dashboard with nothing behind it:
     * every request is answered from `src/sample`, and nothing is sent
     * anywhere. Off unless `VITE_SAMPLE_DATA=true`.
     */
    SAMPLE_DATA: boolean;
    /**
     * PostHog's project token, `VITE_POSTHOG_PROJECT_TOKEN`. Unset, nothing is sent:
     * a local build, the sample build and the tests capture nothing.
     */
    POSTHOG_KEY: string | null;
    /** Where PostHog is, `VITE_POSTHOG_HOST`. EU cloud by default. */
    POSTHOG_HOST: string;
    /**
     * The home, `VITE_SITE_URL`, with no trailing slash: the webapp's own
     * public address. The canonical links and the sitemap name it
     * (`src/static`), and signing out lands on it.
     */
    SITE_URL: string;
    /** Public Cloudflare Turnstile site key for the contact form. */
    TURNSTILE_SITE_KEY: string | null;
    /**
     * Where Google Ads counts a purchase: the account's tag id,
     * `VITE_GOOGLE_ADS_ID` (`AW-` and digits), and each conversion's label,
     * `VITE_GOOGLE_ADS_SIGNUP_LABEL`, `VITE_GOOGLE_ADS_PLAN_LABEL` and
     * `VITE_GOOGLE_ADS_PACK_LABEL`. Null
     * without a well-formed id, and in the sample: then no Google script
     * loads (src/lib/ads/google.ts).
     */
    GOOGLE_ADS: GoogleAdsConfig | null;
    /**
     * Where ChatGPT Ads counts a sign-up or a paid plan: the Measurement Pixel's id,
     * `VITE_OPENAI_ADS_PIXEL_ID`, from the Conversions tab of Ads Manager.
     * Null without a well-formed id, and in the sample: then no OpenAI
     * script loads (src/lib/ads/openai.ts).
     */
    OPENAI_ADS_PIXEL: string | null;
    /**
     * Where Meta counts a sign-up, a paid plan or a pack: the Meta pixel's id,
     * `VITE_META_PIXEL_ID`, from Events Manager. Null without a
     * well-formed id, and in the sample: then no Meta script loads
     * (src/lib/ads/meta.ts).
     */
    META_ADS_PIXEL: string | null;
    /**
     * Where the build's hashed files are served from, `VITE_ASSET_URL`: an
     * https origin such as https://assets.jobenvoy.ai, with no path. Null
     * when unset, and then they are this origin's own. As written: the
     * build refuses a malformed value (scripts/assetUrl.ts) before it is
     * built, and the Content Security Policy checks it again (`src/static`).
     */
    ASSET_URL: string | null;
}

export interface GoogleAdsConfig {
    id: string;
    signupLabel: string | null;
    planLabel: string | null;
    packLabel: string | null;
}

function requireEnvVar(name: string): string {
    const value = import.meta.env[name];
    if (!value) {
        throw new Error(`Environment variable ${name} is not defined`);
    }
    return value;
}

function optionalEnvVar(name: string): string | null {
    const value = (import.meta.env[name] as string | undefined)?.trim();
    return value ? value : null;
}

/** Google Ads' ids and labels, when the id is one: anything else loads nothing. */
function googleAds(): GoogleAdsConfig | null {
    const id = optionalEnvVar("VITE_GOOGLE_ADS_ID");
    if (!id || !/^AW-\d+$/.test(id)) return null;
    return {
        id,
        signupLabel: optionalEnvVar("VITE_GOOGLE_ADS_SIGNUP_LABEL"),
        planLabel: optionalEnvVar("VITE_GOOGLE_ADS_PLAN_LABEL"),
        packLabel: optionalEnvVar("VITE_GOOGLE_ADS_PACK_LABEL"),
    };
}

/** The pixel id, when it is one: letters, digits, `_` and `-`. Anything else loads nothing. */
function openaiAdsPixel(): string | null {
    const id = optionalEnvVar("VITE_OPENAI_ADS_PIXEL_ID");
    return id && /^[A-Za-z0-9_-]+$/.test(id) ? id : null;
}

/** The pixel id, when it is one: digits only. Anything else loads nothing. */
function metaAdsPixel(): string | null {
    const id = optionalEnvVar("VITE_META_PIXEL_ID");
    return id && /^\d+$/.test(id) ? id : null;
}

function origin(name: string, fallback: string): string {
    return (optionalEnvVar(name) ?? fallback).replace(/\/+$/, "");
}

/**
 * The sample build, and the build the Playwright synthetics run. Read
 * from `import.meta.env` by name, so Vite writes the value into the
 * code and a production build drops every branch behind them, and the
 * sample and the synthetics' sign-in with it.
 */
export const SAMPLE_DATA: boolean = import.meta.env.VITE_SAMPLE_DATA === "true";

/**
 * The synthetics' build: a fixed person signed in (or out) instead of
 * AuthKit, and the real API client, whose requests the synthetics answer
 * from the API mock. Only in a build made with `--mode e2e`; vite.config
 * refuses the flag in any other.
 */
export const E2E: boolean = import.meta.env.MODE === "e2e" && import.meta.env.VITE_E2E === "true";

/**
 * The site build: the signed-out pages for jobenvoy.ai, prerendered
 * (`vite build --mode site`, see `src/site`). It signs no one in, so it
 * needs no AuthKit client.
 */
export const SITE: boolean = import.meta.env.MODE === "site";

const sampleData = SAMPLE_DATA;

export const env: Env = {
    API_URL: sampleData
        ? (optionalEnvVar("VITE_API_URL") ?? "")
        : origin("VITE_API_URL", "https://api.jobenvoy.ai"),
    WORKOS_CLIENT_ID:
        sampleData || SITE
            ? (optionalEnvVar("VITE_WORKOS_CLIENT_ID") ?? "")
            : requireEnvVar("VITE_WORKOS_CLIENT_ID"),
    WORKOS_API_HOSTNAME: optionalEnvVar("VITE_WORKOS_API_HOSTNAME") ?? "api.workos.com",
    SAMPLE_DATA: sampleData,
    POSTHOG_KEY: sampleData
        ? null
        : (optionalEnvVar("VITE_POSTHOG_PROJECT_TOKEN") ?? optionalEnvVar("VITE_POSTHOG_KEY")),
    POSTHOG_HOST: optionalEnvVar("VITE_POSTHOG_HOST") ?? "https://eu.i.posthog.com",
    SITE_URL: origin("VITE_SITE_URL", "https://jobenvoy.ai"),
    TURNSTILE_SITE_KEY: optionalEnvVar("VITE_TURNSTILE_SITE_KEY"),
    GOOGLE_ADS: sampleData ? null : googleAds(),
    OPENAI_ADS_PIXEL: sampleData ? null : openaiAdsPixel(),
    META_ADS_PIXEL: sampleData ? null : metaAdsPixel(),
    ASSET_URL: optionalEnvVar("VITE_ASSET_URL"),
};
